import React, { useState } from "react";
import Dropzone from "react-dropzone";

const ImageUploader = ({
  images = [],
  StorageFolder,
  folderName,
  uploadFileToServer,
  onChange,
  maxImages = 4, // Default to 4 if not passed
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const onDrop = (acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];

    if (images.length >= maxImages) {
      alert(`Max ${maxImages} images allowed`);
      return;
    }
    if (file.size / 1024 > 300) {
      alert("File size must be under 300 KB");
      return;
    }

    setLoading(true);
    const fileName = new Date().getTime() + "_" + file.name;
    const storagePath = StorageFolder + "/" + folderName;

    uploadFileToServer(file, storagePath, fileName)
      .then(function() {
        const bucket = "posible.in/";
        const url =
          "https://storage.googleapis.com/" +
          bucket +
          storagePath +
          "/" +
          fileName;
        const newImage = {
          ImageName: fileName,
          ImageSize: file.size,
          ImagePath: url,
        };
        onChange(images.concat(newImage));
      })
      .catch(function() {
        alert("Upload failed");
      })
      .finally(function() {
        setLoading(false);
      });
  };

  const removeImage = (idx) => {
    const arr = [...images];
    arr.splice(idx, 1);
    onChange(arr);
  };

  const moveImage = (idx, dir) => {
    const arr = [...images];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    const temp = arr[idx];
    arr[idx] = arr[target];
    arr[target] = temp;
    onChange(arr);
  };

  // Check if we should show the upload box
  const shouldShowUploadBox = images.length < maxImages;

  return (
    <div style={{ fontFamily: "sans-serif", color: "#333" }}>
      <div style={gridContainer}>
        {/* 1. Modern Upload Box - Only show if maxImages not reached */}
        {shouldShowUploadBox && (
          <Dropzone
            onDrop={onDrop}
            multiple={false}
            style={uploadBoxStyle}
            disableClick={loading}
          >
            <div style={{ textAlign: "center" }}>
              {loading ? (
                <div className="spinner-border text-primary" role="status">
                  <i className="fa fa-circle-o-notch fa-spin fa-2x"></i>
                </div>
              ) : (
                <>
                  <i
                    className="fa fa-plus-circle fa-2x"
                    style={{ color: "#007bff" }}
                  ></i>
                  <div
                    style={{
                      marginTop: "8px",
                      fontWeight: "500",
                      fontSize: "13px",
                    }}
                  >
                    Add {folderName}
                  </div>
                </>
              )}
            </div>
          </Dropzone>
        )}

        {/* 2. Image Gallery */}
        {images.map((img, idx) => (
          <div key={idx} className="img-card-wrapper" style={cardStyle}>
            <img src={img.ImagePath} style={imgStyle} alt="upload" />

            {/* Hover Overlay - This makes it look modern */}
            <div className="img-overlay" style={overlayStyle}>
              <div style={actionRow}>
                <button
                  onClick={() => setSelectedImage(img.ImagePath)}
                  style={iconBtn}
                  title="View"
                >
                  <i className="fa fa-search-plus"></i>
                </button>
                <button
                  onClick={() => removeImage(idx)}
                  style={iconBtnDanger}
                  title="Delete"
                >
                  <i className="fa fa-trash"></i>
                </button>
              </div>
              <div style={arrowRow}>
                <button
                  onClick={() => moveImage(idx, -1)}
                  style={moveBtn}
                  disabled={idx === 0}
                >
                  <i className="fa fa-chevron-left"></i>
                </button>
                <button
                  onClick={() => moveImage(idx, 1)}
                  style={moveBtn}
                  disabled={idx === images.length - 1}
                >
                  <i className="fa fa-chevron-right"></i>
                </button>
              </div>
            </div>

            {idx === 0 && <div style={mainBadge}>PRIMARY</div>}
          </div>
        ))}
      </div>

      {/* 3. Lightbox (Fullscreen View) */}
      {selectedImage && (
        <div onClick={() => setSelectedImage(null)} style={lightboxBg}>
          <img src={selectedImage} style={lightboxImg} alt="Large" />
          <div style={closeBtn}>&times;</div>
        </div>
      )}

      {/* Minimal CSS for the Hover effect */}
      <style>{`
        .img-card-wrapper:hover .img-overlay { opacity: 1 !important; }
        .img-card-wrapper { transition: transform 0.2s; }
        .img-card-wrapper:hover { transform: translateY(-3px); }
      `}</style>
    </div>
  );
};

// --- STYLES (Clean & Flat UI) ---
const gridContainer = {
  display: "flex",
  flexWrap: "wrap",
  gap: "15px",
  alignItems: "flex-start",
};

const uploadBoxStyle = {
  width: "120px",
  height: "120px",
  border: "2px dashed #d1d9e6",
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  background: "#f8faff",
  color: "#007bff",
  transition: "all 0.3s",
};

const cardStyle = {
  width: "120px",
  height: "120px",
  borderRadius: "12px",
  overflow: "hidden",
  position: "relative",
  border: "1px solid #eee",
  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
};

const imgStyle = { width: "100%", height: "100%", objectFit: "cover" };

const overlayStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: "rgba(0,0,0,0.4)",
  opacity: 0,
  transition: "opacity 0.3s",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: "8px",
};

const actionRow = { display: "flex", justifyContent: "space-between" };
const arrowRow = { display: "flex", justifyContent: "center", gap: "10px" };

const iconBtn = {
  background: "#fff",
  border: "none",
  borderRadius: "6px",
  width: "28px",
  height: "28px",
  cursor: "pointer",
  color: "#333",
};
const iconBtnDanger = { ...iconBtn, color: "#dc3545" };
const moveBtn = {
  background: "rgba(255,255,255,0.9)",
  border: "none",
  borderRadius: "50%",
  width: "24px",
  height: "24px",
  cursor: "pointer",
  fontSize: "10px",
};

const mainBadge = {
  position: "absolute",
  bottom: "5px",
  left: "5px",
  background: "#28a745",
  color: "#fff",
  fontSize: "8px",
  padding: "2px 5px",
  borderRadius: "4px",
  fontWeight: "bold",
};

const lightboxBg = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: "rgba(0,0,0,0.95)",
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const lightboxImg = {
  maxWidth: "90%",
  maxHeight: "90%",
  borderRadius: "8px",
  boxShadow: "0 0 40px rgba(0,0,0,0.5)",
};
const closeBtn = {
  position: "absolute",
  top: "20px",
  right: "30px",
  color: "#fff",
  fontSize: "40px",
  cursor: "pointer",
};

export default ImageUploader;